import _extends from "@babel/runtime/helpers/extends";
import { useEffect } from "react";
import { useCallback, useState } from "react";
import { Form, Spin, App, Select, Alert, Input } from "antd";
import { observer } from "mobx-react";
import { _i, __ } from "../../../../utils";
import { CheckoutTerms } from "../../../checkout";
import { RealCommerceError } from "../../../../exceptions";
import { useRealCommerceErrorHandling } from "../../../../hooks";
const CHANGE_PLAN_FORM_LAYOUT = {
  labelCol: {
    span: 8
  },
  wrapperCol: {
    span: 16
  }
};
const ChangePlanForm = observer(_ref => {
  let {
    record,
    onSave
  } = _ref;
  const {
    message
  } = App.useApp();
  const [busy, setBusy] = useState(false);
  const initialValues = {
    productPlanId: "",
    terms: false,
    privacy: false
  };
  const {
    seller,
    manufacturer,
    busy: subscriptionBusy,
    changePlan,
    lastAppliedCoupon,
    data: {
      id,
      productPlan: {
        id: currentProductPlanId,
        description,
        name
      }
    }
  } = record;
  const [form] = Form.useForm();
  const handleRealCommerceError = useRealCommerceErrorHandling();
  const handleSave = useCallback(async _ref2 => {
    let {
      productPlanId
    } = _ref2;
    try {
      const [newPlan] = changePlan.filter(_ref3 => {
        let {
          data: {
            productPlan: {
              id
            }
          }
        } = _ref3;
        return id === productPlanId;
      });
      await newPlan.apply();
      form.resetFields();
      message.success(__("Your subscription has been updated!"));
      onSave === null || onSave === void 0 ? void 0 : onSave();
    } catch (e) {
      if (e instanceof RealCommerceError) {
        handleRealCommerceError(e);
      }
      throw e;
    }
  }, [record, changePlan]);
  const onFinish = useCallback(async values => {
    setBusy(true);
    try {
      await handleSave(values);
    } catch (e) {
      // Silence is golden.
    } finally {
      setBusy(false);
    }
  }, [form, handleSave]);
  const handleCouponApply = useCallback(async coupon => {
    try {
      await record.fetchChangePlans(coupon);
    } catch (e) {
      if (e instanceof RealCommerceError) {
        handleRealCommerceError(e);
      }
    }
  }, [record]);
  useEffect(() => {
    record.fetchChangePlans();
  }, [currentProductPlanId]);
  return /*#__PURE__*/React.createElement(Spin, {
    spinning: busy || subscriptionBusy
  }, /*#__PURE__*/React.createElement(Form, _extends({
    name: `change-plan-form-${id}`,
    id: `change-plan-form-${id}`,
    form: form
  }, CHANGE_PLAN_FORM_LAYOUT, {
    initialValues: initialValues,
    onFinish: onFinish,
    labelWrap: true
  }), /*#__PURE__*/React.createElement("p", {
    style: {
      marginBottom: 15
    }
  }, __("You can upgrade and downgrade your plan at any time. If you need licenses for more websites, you can get the licenses with an upgrade!")), /*#__PURE__*/React.createElement(Form.Item, {
    label: __("Current plan")
  }, /*#__PURE__*/React.createElement("strong", null, name, ": ", description)), /*#__PURE__*/React.createElement(Form.Item, {
    label: __("Coupon")
  }, lastAppliedCoupon ? /*#__PURE__*/React.createElement(Input, {
    disabled: true,
    value: lastAppliedCoupon,
    addonAfter: /*#__PURE__*/React.createElement("a", {
      onClick: () => handleCouponApply(undefined)
    }, __("Remove coupon"))
  }) : /*#__PURE__*/React.createElement(Input, {
    addonAfter: /*#__PURE__*/React.createElement("a", {
      onClick: e => handleCouponApply(e.target.parentElement.previousElementSibling.value)
    }, __("Apply coupon"))
  })), /*#__PURE__*/React.createElement(Form.Item, {
    label: __("New plan"),
    name: "productPlanId",
    required: true,
    rules: [{
      required: true,
      message: __("Please select a plan!")
    }]
  }, /*#__PURE__*/React.createElement(Select, {
    loading: !(changePlan !== null && changePlan !== void 0 && changePlan.length)
  }, changePlan === null || changePlan === void 0 ? void 0 : changePlan.map(_ref4 => {
    let {
      data: {
        productPlan: {
          id,
          name,
          description
        }
      }
    } = _ref4;
    return /*#__PURE__*/React.createElement(Select.Option, {
      key: id,
      value: id
    }, name, ": ", description);
  }))), /*#__PURE__*/React.createElement(Form.Item, {
    labelCol: {
      span: 0
    },
    wrapperCol: {
      span: 24
    },
    noStyle: true,
    shouldUpdate: (prevValues, nextValues) => prevValues.productPlanId !== nextValues.productPlanId
  }, _ref5 => {
    let {
      getFieldValue
    } = _ref5;
    const productPlanId = getFieldValue("productPlanId");
    if (!productPlanId) {
      return null;
    }
    const [newPlan] = changePlan.filter(_ref6 => {
      let {
        data: {
          productPlan: {
            id
          }
        }
      } = _ref6;
      return id === productPlanId;
    });
    if (!newPlan) {
      // No yet loaded...
      return null;
    }
    const {
      labelImmediatePrice,
      labelPrice,
      data: {
        discount,
        type,
        productPlan: {
          additionalTeachings
        }
      }
    } = newPlan;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Alert, {
      type: "info",
      showIcon: true,
      message: type === "downgrade" ? __("Downgrading") : __("Upgrading"),
      description: type === "downgrade" ? `${__("Please note that the downgrade will take effect immediately. Any fees already paid until the end of the current billing period will not be refunded.")}${lastAppliedCoupon ? ` ${__("You have entered a coupon above, but it does not apply to downgrades.")}` : ""}` : _i(__("Please note that the upgrade will take effect immediately. {{strong}}We will only charge the additional costs for the upgrade within this invoicing period proportionately{{/strong}} from today until the end of the current invoicing period."), {
        strong: /*#__PURE__*/React.createElement("strong", null)
      })
    }), type === "upgrade" && /*#__PURE__*/React.createElement(React.Fragment, null, !!lastAppliedCoupon && !discount && /*#__PURE__*/React.createElement(Alert, {
      type: "warning",
      style: {
        marginTop: 15
      },
      showIcon: true,
      message: __("Coupon not applied!"),
      description: __("Coupon is not valid for this plan. You might choose another one!")
    }), /*#__PURE__*/React.createElement("p", {
      style: {
        marginTop: 15
      }
    }, __("After the upgrade you will have the following costs (incl. VAT):")), /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("strong", null, __("Expected costs today:"), " "), /*#__PURE__*/React.createElement("span", {
      dangerouslySetInnerHTML: {
        __html: labelImmediatePrice
      }
    }), /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement("strong", null, __("Costs for renewal:"), " "), /*#__PURE__*/React.createElement("span", {
      dangerouslySetInnerHTML: {
        __html: labelPrice
      }
    }))), /*#__PURE__*/React.createElement(CheckoutTerms, {
      seller: seller,
      manufacturer: manufacturer,
      formItemAttr: {
        labelCol: {
          span: 24
        },
        wrapperCol: {
          span: 24
        },
        style: {
          marginBottom: 0
        }
      },
      newsletter: false,
      additionalTeachings: additionalTeachings
    }));
  })));
});
export { ChangePlanForm };